import { useQueryClient } from '@tanstack/react-query';
import { Button } from '@toggle/design-system';
import { useScrollListener } from '@toggle/helpers';
import { EventFeedEvent } from '@toggle/toggle';
import React, { useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';

import {
  QUERY_KEY_EVENT_FEED,
  useEventFeed,
  useMarkEventRead,
} from '~/react-query-hooks/events/use-event-feed/useEventFeed';
import { appPaths } from '~/routes/app-paths';
import { POSTHOG_TRACKING_SOURCES } from '~/services/tracking/posthog/constants';
import { usePosthogEvents } from '~/services/tracking/posthog/use-posthog-events/usePosthogEvents';

import { CatalystRow } from './components/catalyst-row/CatalystRow';
import { EarningsRow } from './components/earnings-row/EarningsRow';
import { ScenarioRow } from './components/scenario-row/ScenarioRow';
import * as S from './OverviewEventFeed.styles';
import {
  formatDateHeader,
  getLocalDateKey,
  getStartDate,
  PERIOD_DAYS,
} from './utils';
import {
  StreamFilterTabs,
  tabTypeToEventFeedType,
} from '~/stores/use-stream-filters/useStreamFilters';

export interface OverviewEventFeedProps {
  entityTag: string;
}

export const OverviewEventFeed = ({ entityTag }: OverviewEventFeedProps) => {
  const { t } = useTranslation(['whatsNew', 'earnings', 'insights', 'stream']);
  const [activeTab, setActiveTab] = useState<StreamFilterTabs>('all');
  const navigate = useNavigate();
  const { trackStreamOpen } = usePosthogEvents();
  const feedWrapperRef = useRef<HTMLDivElement>(null);
  const queryClient = useQueryClient();

  const payload = {
    entities: [entityTag],
    type: tabTypeToEventFeedType[activeTab],
    date_from: getStartDate().toISOString(),
  };

  const {
    data,
    isFetching: isLoading,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
  } = useEventFeed(payload);

  const { mutate: markEventRead } = useMarkEventRead();

  const allEvents = useMemo(
    () =>
      data?.pages?.flatMap(page =>
        page.events.filter(event => event.type !== 'market-overview')
      ) ?? [],
    [data?.pages]
  );

  useScrollListener(
    feedWrapperRef,
    () => {
      if (hasNextPage && !isFetchingNextPage) {
        fetchNextPage();
      }
      return hasNextPage;
    },
    [hasNextPage, isFetchingNextPage],
    { isGlobalScrollListened: false, offset: 100 }
  );

  const tabs: Record<Exclude<StreamFilterTabs, 'summaries'>, string> = {
    all: t('stream:streamTypeTabs.all'),
    catalysts: t('stream:streamTypeTabs.catalyst'),
    scenarios: t('stream:streamTypeTabs.scenario'),
    earnings: t('stream:streamTypeTabs.earnings'),
  };

  const handleExpandClick = () => {
    trackStreamOpen(POSTHOG_TRACKING_SOURCES.ASSET_OVERVIEW);
    navigate(appPaths.stream);
  };

  const handleTabChange = (_index: number, tabKey: string) => {
    queryClient.removeQueries({
      queryKey: [QUERY_KEY_EVENT_FEED, payload],
      exact: false,
    });
    setActiveTab(tabKey as StreamFilterTabs);
  };

  const renderEventRow = (event: EventFeedEvent, isLast: boolean) => {
    const props = {
      event,
      isLast,
      onMarkRead: markEventRead,
    };
    switch (event.type) {
      case 'earnings-insight':
        return <EarningsRow {...props} />;
      case 'attribution-insight':
        return <CatalystRow {...props} />;
      case 'scenario-insight':
        return <ScenarioRow {...props} />;
      default:
        return null;
    }
  };

  return (
    <S.Container>
      <S.Header>
        <S.Title>{t('whatsNew:lastNDays', { count: PERIOD_DAYS })}</S.Title>
        <S.ButtonContainer>
          <Button
            variant="tertiary"
            size="small"
            iconName="Expand"
            onClick={handleExpandClick}
          />
        </S.ButtonContainer>
      </S.Header>
      <S.StyledTabLines
        activeTab={activeTab}
        onChange={handleTabChange}
        tabs={Object.entries(tabs).map(([key, value]) => ({
          tabKey: key,
          tabLabel: value,
        }))}
      />
      {isLoading && !data?.pages?.length ? (
        <S.StyledLoader />
      ) : allEvents.length === 0 ? (
        <S.EmptyState>
          <S.EmptyStateText>{t('whatsNew:noInsights')}</S.EmptyStateText>
        </S.EmptyState>
      ) : (
        <S.FeedWrapper ref={feedWrapperRef}>
          {allEvents.map((event, index) => {
            const currentDateKey = getLocalDateKey(event.created_at);
            const prevDateKey =
              index > 0
                ? getLocalDateKey(allEvents[index - 1].created_at)
                : null;
            const showDateHeader = currentDateKey !== prevDateKey;
            const isLast = index === allEvents.length - 1 && !hasNextPage;

            return (
              <React.Fragment key={event.id}>
                {showDateHeader && (
                  <S.DateHeader>
                    <S.DateText>
                      {formatDateHeader(event.created_at, t)}
                    </S.DateText>
                  </S.DateHeader>
                )}
                {renderEventRow(event, isLast)}
              </React.Fragment>
            );
          })}
          {isFetchingNextPage ? (
            <S.PaginationLoader />
          ) : hasNextPage ? null : (
            <S.EndOfFeedWrapper>
              <S.EndOfFeedLine />
              <S.EndOfFeedLabel>{t('whatsNew:endOfFeed')}</S.EndOfFeedLabel>
              <S.EndOfFeedLine />
            </S.EndOfFeedWrapper>
          )}
        </S.FeedWrapper>
      )}
    </S.Container>
  );
};
